Ext.define('SA.data.ScatterPlotView', {
    alias: 'view.scatterPlot',
    extend: 'SA.data.ReportView',

    mixins: {
        filterable: 'SA.data.mixin.Filterable',
        locations: 'SA.data.mixin.OneLocation'
    },

    statics: {
        create({project, censusRelease, name, locationItems, attributes}) {
            var attributes = attributes.filter(id => this.supportsAttribute(id)),
                data = {
                    type: 'scatterPlot',
                    buildingBlocksCensusRelease: censusRelease,
                    filterVisibility: 'strikeout',
                    lineOfBestFitEnabled: true,
                    locationItem: locationItems[0] || null,
                    name,
                    pointColor: '80bbff',
                    pointRadius: 3,
                    title: name,
                    xAttribute: attributes[0] || null,
                    yAttribute: attributes[1] || null
                };

            return new SA.data.ScatterPlotView({project, data});
        },

        getControllers() {
            var views = SA.data.Projects.getViewsOfType('scatterPlot').filter(view => view.hasController());

            return views.map(view => view.getController());
        },

        getMaxTitleLength() {
            return 200;
        },

        supportsAttribute(attribute) {
            return SA.data.AttributesCache.isPolygonAttribute(attribute);
        }
    },

    locationProperty: 'locationItem',

    addAttributeImpl(attribute) {
        if (!this.hasXAttribute()) {
            this.setXAttribute(attribute);
        }
        else {
            this.setYAttribute(attribute);
        }

        this.project.addAttribute(attribute);
    },

    canResolveGeographicRelease() {
        return SA.data.DataModel.canResolveGeographicRelease({attributes: this.getAttributes(), location: this.getLocation()});
    },

    canResolveGeographicUnit() {
        return SA.data.DataModel.canResolveGeographicUnit({attributes: this.getAttributes(), location: this.getLocation()});
    },

    createController() {
        return new SA.ScatterPlotController(this);
    },

    clearXAttribute() {
        this.setXAttribute(null);
    },

    clearYAttribute() {
        this.setYAttribute(null);
    },

    createDataMap() {
        this.syncGeographicReleases();
        this.syncGeographicUnits();

        return new SA.data.scatterplot.ScatterPlotDataMap({
            attributes: this.getAttributes(),
            filter: this.getFilterVisibility() !== 'disable' ? this.getFilterQuery() : null,
            geographicReleases: this.geographicReleases,
            geographicUnit: this.getGeographicUnit(),
            location: this.getQueryLocation()
        });
    },

    getAttributes() {
        return Ext.Array.unique(Ext.Array.clean([this.getXAttribute(), this.getYAttribute()]));
    },

    getDatasetsWithNoMatchingUnitsForLocation() {
        var location = this.getLocation(),
            attributes = this.getAttributes();

        return SA.data.DataModel.getDatasetsWithNoMatchingUnitsForLocation({location, attributes});
    },

    getPointColor() {
        return this.data.pointColor;
    },

    getPointRadius() {
        return this.data.pointRadius;
    },

    getTitle() {
        return this.data.title;
    },

    getXAttribute() {
        return this.data.xAttribute;
    },

    getYAttribute() {
        return this.data.yAttribute;
    },

    getAllowedGeographicUnits() {
        if (!this.hasLocation()) {
            return [];
        }

        var attributes = this.getAttributes(),
            location = this.getLocation(),
            discoverableOrSelectedUnits = this.getDiscoverableOrSelectedGeoUnits(),
            allowedUnits = SA.data.GeographicUnits.getAllowedGeographicUnitsForLocationAndAttributes(location, attributes);

        return allowedUnits.filter(({geographicUnit}) => discoverableOrSelectedUnits.includes(geographicUnit));
    },

    getDiscoverableOrSelectedGeoUnits() {
        var {clean, unique} = Ext.Array;

        return clean(unique([this.getGeographicUnit(), ...this.getDiscoverableGeoUnits()]));
    },

    getFilterableAttributes() {
        var unit = this.getGeographicUnit(),
            attributes = this.getAttributes(),
            projectAttributes = this.getProject().getAttributes().filter(id => {
                var item = SA.data.AttributesCache.get(id);

                return !attributes.includes(id) && this.supportsAttribute(id) && item.geographicUnits.includes(unit);
            });

        return attributes.concat(projectAttributes);
    },

    getGeographicUnit() {
        return this.data.geographicUnit;
    },

    async getReportData() {
        var dataMap, result;

        if (!this.canResolveGeographicRelease() || !this.canResolveGeographicUnit()) {
            return SA.data.scatterplot.ScatterPlotDataMap.getEmptyData();
        }

        this.syncGeographicUnits();
        this.syncGeographicReleases();

        dataMap = this.getDataMap();

        result = await dataMap.getDataPromise();

        if (dataMap !== this.getDataMap()) {
            return await this.getReportData();
        }

        return result;
    },

    hasGeographicUnit() {
        return !!this.data.geographicUnit;
    },

    hasLocation() {
        return !!this.getLocation();
    },

    hasXAttribute() {
        return !!this.getXAttribute();
    },

    hasYAttribute() {
        return !!this.getYAttribute();
    },

    isLineOfBestFitEnabled() {
        return this.data.lineOfBestFitEnabled;
    },

    isSavable() {
        return this.hasLocation() && this.hasXAttribute() && this.hasYAttribute() && this.hasGeographicUnit();
    },

    onCustomLocationUpdateImpl(location) {
        this.getDataMap().removeLocation(SA.data.LocationsCache.normalizeLocation(location));
    },

    removeAttributeImpl(attribute) {
        if (attribute === this.getXAttribute()) {
            this.clearXAttribute();
        }

        if (attribute === this.getYAttribute()) {
            this.clearYAttribute();
        }
    },

    setAttributeForAxis({axis, attribute}) {
        axis === 'x'
            ? this.setXAttribute(attribute)
            : this.setYAttribute(attribute);
    },

    setFilterImpl(filter) {
        var changed = this.mixins.filterable.setFilterImpl.call(this, filter);

        if (changed) {
            this.clearDataMap();
        }

        return changed;
    },

    setFilterVisibilityImpl(visibility) {
        var changed = this.mixins.filterable.setFilterVisibilityImpl.call(this, visibility);

        if (changed) {
            this.clearDataMap();
        }

        return changed;
    },

    setGeographicUnit(geographicUnit) {
        if (this.set('geographicUnit', geographicUnit)) {
            this.auditFilter();
            this.clearDataMap();
            this.fireEvent('change:geographicUnit', this);
        }
    },

    setLineOfBestFitEnabled(enabled) {
        this.setWithEvent('lineOfBestFitEnabled', enabled);
    },

    setLocationImpl(location) {
        this.mixins.locations.setLocationImpl.call(this, location);

        this.clearDataMap();

        if (location) {
            this.project.addLocation(location);
        }
    },

    setPointColor(color) {
        this.setWithEvent('pointColor', color);
    },

    setPointRadius(radius) {
        this.setWithEvent('pointRadius', radius);
    },

    setTitle(title) {
        this.set('title', title.trim());

        this.fireEvent('change:title', this);
    },

    setXAttribute(attribute) {
        if (this.set('xAttribute', attribute)) {
            this.fireEvent('change:attribute', this);

            this.auditFilter();
            this.clearDataMap();
        }
    },

    setYAttribute(attribute) {
        if (this.set('yAttribute', attribute)) {
            this.fireEvent('change:attribute', this);

            this.auditFilter();
            this.clearDataMap();
        }
    },

    syncGeographicReleases() {
        var releases = SA.data.AttributesCache.getGeographicReleasesForAttributes(this.getAttributes()).sort();

        if (SA.Json.differ(this.geographicReleases, releases)) {
            this.geographicReleases = releases;

            this.clearDataMap();
        }
    },

    syncGeographicUnits() {
        var allowedUnits = Ext.Array.pluck(this.getAllowedGeographicUnits(), 'id'),
            first = allowedUnits[0],
            second = allowedUnits[1] || first,
            location = SA.data.LocationsCache.get(this.getLocation());

        if (!allowedUnits.length || !location) {
            this.setGeographicUnit(null);
        }
        else if (!allowedUnits.includes(this.getGeographicUnit())) {
            this.setGeographicUnit(first === location.geographicUnit ? second : first);
        }
    }
});
